/**
 * Unified Analytics — GA4/GTM + Mixpanel
 *
 * Mirrors the old project's `src/utils/analytics.ts`:
 * - initGA4(measurementId) — injects gtag.js, configures GA4
 * - logEvent, setUserProperties, trackPageView — gtag helpers
 * - trackEvent(eventName, eventParams) — pushes to dataLayer AND Mixpanel
 * - MIXPANEL_EVENT_NAME_MAP for snake_case → Title Case mapping
 *
 * Usage:
 *   import { initGA4, trackEvent } from '@/utils/analytics'
 *
 *   // in main.tsx:
 *   initGA4('G-CTX52JH1QF')
 *
 *   // anywhere else:
 *   trackEvent('dashboard_loaded', { user_role: 'admin' })
 */

import { IS_MIXPANEL_ENABLED, mixpanel } from '@/config/mixpanel'

// ─── Types ───────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>
    gtag: (...args: unknown[]) => void
  }
}

// ─── GA4 initialisation ──────────────────────────────────────────────────────

/**
 * Initialise Google Analytics 4 via gtag.js.
 * Call once in `main.tsx` before render.
 */
export function initGA4(measurementId: string): void {
  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || []

  // Define gtag function
  window.gtag = function gtag(...args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(Object.assign({}, ...args.map((a, i) => (i === 0 ? { '0': a } : typeof a === 'object' ? a : { [i]: a }))))
  }

  // Simpler, standard gtag push
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>)
  }
  window.gtag = gtag as unknown as (...args: unknown[]) => void

  // Timestamp
  window.gtag('js', new Date())

  // Config GA4
  window.gtag('config', measurementId, { send_page_view: true })

  // Inject gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  if (import.meta.env.DEV) {
    console.info('[GA4] Initialized', { measurementId })
  }
}

// ─── gtag helpers ────────────────────────────────────────────────────────────

/**
 * Log a custom event via gtag.
 */
export function logEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params)
  }
}

/**
 * Set user properties for GA4.
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', properties)
  }
}

/**
 * Track a page view via gtag.
 */
export function trackPageView(pageTitle: string, pagePath: string): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_path: pagePath,
    })
  }
}

// ─── Mixpanel event name mapping ─────────────────────────────────────────────

/**
 * Maps internal snake_case event names to Mixpanel Title Case names.
 * Unmapped names are sent to Mixpanel as-is.
 */
const MIXPANEL_EVENT_NAME_MAP: Record<string, string> = {
  dashboard_loaded: 'Dashboard Loaded',
  service_requested: 'Service Requested',
  invoice_downloaded: 'Invoice Downloaded',
  report_generated: 'Report Generated',
  ambulance_tracked: 'Ambulance Tracked',
  contract_viewed: 'Contract Viewed',
  user_invited: 'User Invited',
  payment_submitted: 'Payment Processed',
  page_viewed: 'Page Viewed',
  auth_login_success: 'Login Success',
  auth_logout: 'Logout',
  Logout: 'Logout',
  auth_otp_requested: 'OTP Requested',
  auth_otp_verified: 'OTP Verified',
  back_button_clicked: 'Back Button Clicked',
  sign_up: 'Sign Up',
  language_changed: 'Language Changed',
  Call_click: 'Call Click',
  services_clicked: 'Services Clicked',
  service_selected: 'Service Selected',
  service_card_removed: 'Service Card Removed',
  questionnaire_start: 'Questionnaire Start',
  question_answered: 'Question Answered',
  questionnaire_completed: 'Questionnaire Completed',
  questionnaire_abandon: 'Questionnaire Abandon',
  begin_checkout: 'Begin Checkout',
  cupon_code_added: 'Coupon Code Added',
  click_review: 'Click Review',
  sent_review: 'Sent Review',
  // Behavior tracking — clear "page opened" and "action taken"
  'Page Opened': 'Page Opened',
  'Route Change': 'Route Change',
  'Action: Click': 'Action: Click',
  'Action: Scroll': 'Action: Scroll',
  'Action: Form': 'Action: Form',
  'Action: Modal Open': 'Action: Modal Open',
  'Action: Modal Close': 'Action: Modal Close',
  page_view: 'Page View',
  time_on_page: 'Time on Page',
}

// ─── Unified trackEvent ──────────────────────────────────────────────────────

export type TrackEventOptions = {
  /** Sent to Mixpanel only; dataLayer still uses `eventName` (stable for GTM). */
  mixpanelEventName?: string
}

/**
 * Push an event to **dataLayer** (GA4/GTM) **and** Mixpanel (if enabled).
 *
 * This is the one function used by feature code everywhere.
 *
 * @param eventName  Internal event name (snake_case or Title Case)
 * @param eventParams  Arbitrary event properties
 * @param options  Optional; use `mixpanelEventName` for a readable Mixpanel title while keeping GTM `event` stable.
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
  options?: TrackEventOptions
): void {
  // 1. Push to dataLayer for GTM / GA4
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  })

  // 2. Send to Mixpanel (if enabled)
  if (IS_MIXPANEL_ENABLED) {
    const mapped = MIXPANEL_EVENT_NAME_MAP[eventName] || eventName
    const mixpanelEventName = options?.mixpanelEventName ?? mapped
    mixpanel.track(mixpanelEventName, {
      ...eventParams,
      platform: 'web',
      app_name: 'business_dashboard',
      project_type: 'web_dashboard',
      source: 'react_web_app',
    })
  }
}
